import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';

// Load Stripe with publishable key from environment variables
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
  // Fallback for development - replace with your test key in production
  'pk_test_TYooMQauvdEDq54NiTphI7jx');

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [errorMessage, setErrorMessage] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [serverId, setServerId] = useState('');
  const [serverIdError, setServerIdError] = useState('');

  useEffect(() => {
    if (stripe && elements) {
      setIsMounted(true);
    }
  }, [stripe, elements]);

  const validateServerId = (id) => {
    // Discord server IDs are typically 17-19 digits
    const serverIdRegex = /^\d{17,19}$/;
    if (!id) {
      setServerIdError('Please enter your Discord server ID');
      return false;
    } else if (!serverIdRegex.test(id)) {
      setServerIdError('Please enter a valid Discord server ID (17-19 digits)');
      return false;
    }
    setServerIdError('');
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !isMounted) {
      setErrorMessage('Please wait for the payment form to load completely.');
      return;
    }

    // Validate server ID
    if (!validateServerId(serverId)) {
      return;
    }

    setProcessing(true);
    setErrorMessage(null);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment-success?server_id=${encodeURIComponent(serverId)}`,
          payment_method_data: {
            metadata: {
              server_id: serverId
            }
          }
        },
      });

      if (error) {
        setErrorMessage(error.message);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setErrorMessage('An unexpected error occurred. Please try again.');
    }

    setProcessing(false);
  };

  if (!stripe || !elements) {
    return (
      <div className="text-center text-gray-custom-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-custom-400 mx-auto mb-2"></div>
        Loading payment form...
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-md mx-auto">
      <div className="mb-4">
        <label htmlFor="server-id" className="block text-gray-custom-200 mb-2 text-sm font-medium">
          Discord Server ID <span className="text-red-500">*</span>
        </label>
        <div className="relative">
          <input
            id="server-id"
            type="text"
            value={serverId}
            onChange={(e) => setServerId(e.target.value)}
            placeholder="Enter your Discord server ID"
            className="w-full px-4 py-2 bg-gray-custom-800 border border-gray-custom-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-custom-600 text-gray-custom-200"
          />
          {serverIdError && (
            <div className="text-red-500 text-xs mt-1">{serverIdError}</div>
          )}
          <div className="mt-1 text-xs text-gray-custom-400">
            <a
              href="https://support.discord.com/hc/en-us/articles/206346498-Where-can-I-find-my-User-Server-Message-ID-"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-custom-300 hover:text-white underline"
            >
              How to find your server ID
            </a>
          </div>
        </div>
      </div>

      <div className="mb-6 mt-6">
        <label className="block text-gray-custom-200 mb-2 text-sm font-medium">
          Payment Details <span className="text-red-500">*</span>
        </label>
        <PaymentElement
          options={{
            layout: 'tabs',
            defaultValues: {
              billingDetails: {
                name: 'Auto-filled name',
              },
            },
          }}
        />
      </div>

      <div className="mb-4 p-3 bg-gray-custom-800/50 border border-gray-custom-700 rounded-lg text-sm">
        <p className="text-gray-custom-300">
          <span className="font-medium">One-time payment of £1</span> for lifetime premium access to your Discord server.
        </p>
      </div>

      {errorMessage && (
        <div className="text-red-500 mt-4 text-center">
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={!isMounted || processing}
        className="btn btn-primary w-full"
      >
        {processing ? (
          <span className="flex items-center justify-center">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </span>
        ) : (
          'Pay £1 for Lifetime Premium'
        )}
      </button>
    </form>
  );
}

export default function PaymentForm() {
  const [clientSecret, setClientSecret] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the Cloudflare Worker URL for payment intent creation
        const apiUrl = 'https://overseer-payment-worker.yourdomain.workers.dev';

        // For local development, you can use this URL instead:
        // const apiUrl = 'http://localhost:3001/create-payment-intent';

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            amount: 100, // £1 in pence
            metadata: {
              product: 'premium_subscription',
              description: 'Overseer Premium Lifetime'
            }
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to create payment intent');
        }

        setClientSecret(data.clientSecret);
      } catch (err) {
        console.error('Error creating payment intent:', err);
        setError(err.message || 'Failed to initialize payment form. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    createPaymentIntent();
  }, []);

  const appearance = {
    theme: 'night',
    variables: {
      colorPrimary: '#6366f1',
      colorBackground: '#1f2937',
      colorText: '#f3f4f6',
      colorDanger: '#ef4444',
      fontFamily: 'Inter, system-ui, sans-serif',
      spacingUnit: '4px',
      borderRadius: '8px',
    },
  };

  if (loading) {
    return (
      <div className="text-center text-gray-custom-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-custom-400 mx-auto mb-2"></div>
        Initializing payment form...
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center">
        <div className="text-red-500 mb-2">{error}</div>
        <button
          onClick={() => window.location.reload()}
          className="text-gray-custom-400 hover:text-white underline"
        >
          Try again
        </button>
      </div>
    );
  }

  return clientSecret ? (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance,
      }}
    >
      <CheckoutForm />
    </Elements>
  ) : (
    <div className="text-center text-gray-custom-400">
      Loading payment form...
    </div>
  );
}